/**
 * PWA Registration and Update Handler
 * Registers the service worker and handles updates
 */

import { logger } from '@core/logger';

/**
 * Register the service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    logger.log('[PWA] Service workers are not supported');
    return null;
  }

  // Check for secure context (HTTPS or localhost)
  if (!window.isSecureContext) {
    logger.log('[PWA] Service workers require a secure context (HTTPS or localhost)');
    return null;
  }

  try {
    // Determine the correct path based on environment
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // Skip service worker registration in development mode
    if (isDevelopment && import.meta.env.DEV) {
      logger.log('[PWA] Service worker registration skipped in development mode');
      return null;
    }
    const swPath = isDevelopment ? '/dev-sw.js?dev-sw' : '/Chesse/sw.js'; // todo: make this work for preview
    const swScope = isDevelopment ? '/' : '/Chesse/';

    const doRegister = (path: string, opts: RegistrationOptions) =>
      navigator.serviceWorker.register(path, opts)

    // Register the service worker generated by Vite PWA
    const registration = await doRegister(swPath, {
      scope: swScope,
      type: isDevelopment ? 'module' : 'classic'
    }).catch((e) => {
      if (!isDevelopment) {
        throw e;
      }
      return doRegister('/Chesse/sw.js', {
        scope: '/Chesse/',
        type: 'classic'
      });
    });

    logger.log('[PWA] Service worker registered:', registration.scope);

    // Check for updates periodically
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000); // Check every hour

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;

      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker installed, show update notification
            showUpdateNotification();
          }
        });
      }
    });

    return registration;
  } catch (error) {
    logger.error('[PWA] Service worker registration failed:', error);
    return null;
  }
}

/**
 * Show update notification to user
 */
function showUpdateNotification(): void {
  const notification = document.createElement('div');
  notification.id = 'pwa-update-notification';
  notification.innerHTML = `
    <div style="
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: linear-gradient(135deg, #5e315b 0%, #606070 100%);
      color: #ffffeb;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      z-index: 10000;
      max-width: 350px;
      font-family: 'Courier New', monospace;
      border: 2px solid #7e7e8f;
    ">
      <h3 style="margin: 0 0 8px 0; font-size: 16px;">Update Available</h3>
      <p style="margin: 0 0 12px 0; font-size: 14px;">A new version of Chesse is available!</p>
      <div style="display: flex; gap: 8px;">
        <button id="pwa-update-button" style="
          flex: 1;
          padding: 8px 16px;
          background: linear-gradient(135deg, #606070 0%, #7e7e8f 100%);
          color: #ffffeb;
          border: 1px solid #c2c2d1;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-family: inherit;
        ">Update Now</button>
        <button id="pwa-dismiss-button" style="
          padding: 8px 16px;
          background: transparent;
          color: #ffffeb;
          border: 1px solid #7e7e8f;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-family: inherit;
        ">Later</button>
      </div>
    </div>
  `;

  document.body.appendChild(notification);

  // Update button handler
  document.getElementById('pwa-update-button')?.addEventListener('click', () => {
    // Tell service worker to skip waiting
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SKIP_WAITING' });
    }
    window.location.reload();
  });

  // Dismiss button handler
  document.getElementById('pwa-dismiss-button')?.addEventListener('click', () => {
    notification.remove();
  });
}

/**
 * Check if app is running as PWA
 */
export function isPWA(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true;
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Show install prompt if available
 */
export function setupInstallPrompt(): void {
  let deferredPrompt: BeforeInstallPromptEvent | null = null;

  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // Prevent the default prompt
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;

    // Show custom install button/banner
    showInstallBanner(deferredPrompt);
  });

  window.addEventListener('appinstalled', () => {
    logger.log('[PWA] App installed successfully');
    deferredPrompt = null;

    // Hide install banner
    const banner = document.getElementById('pwa-install-banner');
    if (banner) {
      banner.remove();
    }
  });
}

/**
 * Show custom install banner
 */
function showInstallBanner(deferredPrompt: BeforeInstallPromptEvent): void {
  // Don't show if already installed
  if (isPWA()) {
    return;
  }

  // Don't show if user dismissed recently
  const dismissed = localStorage.getItem('pwa-install-dismissed');
  if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) {
    return; // Wait 7 days
  }

  const banner = document.createElement('div');
  banner.id = 'pwa-install-banner';
  banner.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: linear-gradient(135deg, #5e315b 0%, #606070 100%);
      color: #ffffeb;
      padding: 12px 20px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
      z-index: 10000;
      display: flex;
      align-items: center;
      gap: 16px;
      font-family: 'Courier New', monospace;
      border: 2px solid #7e7e8f;
    ">
      <span style="font-size: 14px;">Install Chesse for offline play!</span>
      <button id="pwa-install-button" style="
        padding: 6px 16px;
        background: linear-gradient(135deg, #606070 0%, #7e7e8f 100%);
        color: #ffffeb;
        border: 1px solid #c2c2d1;
        border-radius: 4px;
        cursor: pointer;
        font-size: 13px;
        font-family: inherit;
      ">Install</button>
      <button id="pwa-install-dismiss" style="
        padding: 6px 12px;
        background: transparent;
        color: #ffffeb;
        border: none;
        cursor: pointer;
        font-size: 18px;
      ">×</button>
    </div>
  `;

  document.body.appendChild(banner);

  // Install button handler
  document.getElementById('pwa-install-button')?.addEventListener('click', async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      logger.log('[PWA] Install prompt outcome:', outcome);

      if (outcome === 'accepted') {
        banner.remove();
      }
    }
  });

  // Dismiss button handler
  document.getElementById('pwa-install-dismiss')?.addEventListener('click', () => {
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
    banner.remove();
  });
}

/**
 * Initialize PWA features
 */
export async function initializePWA(): Promise<void> {
  logger.log('[PWA] Initializing...');

  // Only initialize PWA features if service workers are supported and in secure context
  if (!('serviceWorker' in navigator)) {
    logger.log('[PWA] Service workers not supported - skipping PWA initialization');
    return;
  }

  if (!window.isSecureContext) {
    logger.log('[PWA] Not in secure context - skipping PWA initialization');
    return;
  }

  // Register service worker
  await registerServiceWorker();

  // Setup install prompt
  setupInstallPrompt();

  // Log PWA status
  if (isPWA()) {
    logger.log('[PWA] Running as installed PWA');
  } else {
    logger.log('[PWA] Running in browser');
  }

  // Handle online/offline events
  window.addEventListener('online', () => {
    logger.log('[PWA] Back online');
    showConnectionStatus('online');
  });

  window.addEventListener('offline', () => {
    logger.log('[PWA] Gone offline');
    showConnectionStatus('offline');
  });
}

/**
 * Show connection status notification
 */
function showConnectionStatus(status: 'online' | 'offline'): void {
  const existing = document.getElementById('connection-status');
  if (existing) {
    existing.remove();
  }

  const notification = document.createElement('div');
  notification.id = 'connection-status';
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    padding: 12px 24px;
    background: ${status === 'online' ? '#3CA370' : '#eb564b'};
    color: white;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    z-index: 10000;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    animation: slideDown 0.3s ease-out;
  `;
  notification.textContent = status === 'online' ? 'Back Online' : 'Offline Mode';

  document.body.appendChild(notification);

  // Remove after 3 seconds
  setTimeout(() => {
    notification.style.animation = 'slideUp 0.3s ease-out';
    setTimeout(() => notification.remove(), 300);
  }, 3000);
}

// Add animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideDown {
    from {
      transform: translate(-50%, -100%);
      opacity: 0;
    }
    to {
      transform: translate(-50%, 0);
      opacity: 1;
    }
  }
  @keyframes slideUp {
    from {
      transform: translate(-50%, 0);
      opacity: 1;
    }
    to {
      transform: translate(-50%, -100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);
